/**
 * Admin RSVP Reports Page
 * View and export RSVP data with date-mode-specific views
 *
 * Full Week Mode: Weekly summary with M/L counts
 * Single Day Mode: Sign up sheet grouped by pickup location
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useUIStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';
import { DateNavigator, type DateMode } from '@/components/reports/DateNavigator';
import { ReportTable } from '@/components/reports/ReportTable';
import type { ReportView } from '@/components/reports/ReportTable';
import { fetchRSVPReport, exportRSVPReportXLSX } from '@/lib/api/reports';
import { fetchPickupLocations } from '@/lib/api/pickupLocations';
import {
  getCurrentWeekStart,
  getWeekRange,
  toISODate,
} from '@/lib/utils/dateHelpers';
import { LocationColorDot } from '@/components/ui/location-color-dot';
import { Download, FileSpreadsheet } from 'lucide-react';
import type {
  PickupLocation,
  RSVPReportResponse,
  RSVPSummaryItem,
  RSVPReportItem,
  RSVPLocationBreakdownItem,
  CookingDayReportData,
} from '@/types/api';

export function Reports() {
  const { user } = useAuthStore();
  const { showToast } = useUIStore();

  // Date navigation state
  const [currentDate, setCurrentDate] = useState<Date>(() => getCurrentWeekStart());
  const [dateMode, setDateMode] = useState<DateMode>('week');

  // Filter state - location filter only shown in day-like report modes
  const [locationId, setLocationId] = useState<number | undefined>(undefined);

  // View state - automatically set based on date mode
  const isDayLikeMode = dateMode !== 'week';
  const includeMemberDetails = dateMode === 'day';
  const activeTab: ReportView = dateMode === 'week'
    ? 'summary'
    : dateMode === 'day'
      ? 'signupSheet'
      : 'cookingDay';

  // Data state
  const [pickupLocations, setPickupLocations] = useState<PickupLocation[]>([]);
  const [reportData, setReportData] = useState<RSVPReportResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  // Calculate date range based on mode
  const dateRange = useMemo(() => {
    if (dateMode === 'week') {
      const { start, end } = getWeekRange(currentDate);
      return {
        startDate: toISODate(start),
        endDate: toISODate(end),
      };
    } else {
      // Single day mode
      const dateStr = toISODate(currentDate);
      return {
        startDate: dateStr,
        endDate: dateStr,
      };
    }
  }, [currentDate, dateMode]);

  // Load pickup locations on mount
  useEffect(() => {
    const loadPickupLocations = async () => {
      try {
        const locations = await fetchPickupLocations(false);
        setPickupLocations(locations);
      } catch (error) {
        showToast(
          error instanceof Error ? error.message : 'Failed to load pickup locations',
          'error'
        );
      }
    };

    loadPickupLocations();
  }, [showToast]);

  // Load report data
  const loadReportData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetchRSVPReport({
        start: dateRange.startDate,
        end: dateRange.endDate,
        mode: dateMode,
        // Only apply location filter in day-like modes
        locationId: isDayLikeMode ? locationId : undefined,
        // Only fetch per-member detail rows for the single-day sign up sheet
        includeDetails: includeMemberDetails,
      });
      setReportData(response);
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to load report data',
        'error'
      );
      setReportData(null);
    } finally {
      setLoading(false);
    }
  }, [dateRange, dateMode, includeMemberDetails, isDayLikeMode, locationId, showToast]);

  // Reload when filters change
  useEffect(() => {
    loadReportData();
  }, [loadReportData]);

  // Handle XLSX export
  const handleExport = async () => {
    try {
      setExporting(true);
      await exportRSVPReportXLSX({
        start: dateRange.startDate,
        end: dateRange.endDate,
        mode: dateMode,
        locationId: isDayLikeMode ? locationId : undefined,
        // Only single-day exports need the detailed sign up sheet rows
        includeDetails: includeMemberDetails,
      });
      showToast('Report exported successfully', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to export report',
        'error'
      );
    } finally {
      setExporting(false);
    }
  };

  // Handle location filter change
  const handleLocationChange = (value: string) => {
    setLocationId(value === 'ALL' ? undefined : parseInt(value, 10));
  };

  // Handle date mode change
  const handleModeChange = (mode: DateMode) => {
    setDateMode(mode);
    // Reset location filter when switching modes
    setLocationId(undefined);
    // Reset to current week start when switching to week mode
    if (mode === 'week') {
      setCurrentDate(getCurrentWeekStart());
    }
  };

  // Get location name for display
  const getLocationName = (id: number | null | undefined) => {
    if (!id) return 'All Locations';
    const location = pickupLocations.find((loc) => loc.id === id);
    return location?.name || 'Unknown';
  };

  // Get menu info for single day header
  const menuInfo = useMemo(() => {
    if (dateMode !== 'day' || !reportData?.summary?.length) {
      return null;
    }
    const firstSummary = reportData.summary[0];
    return {
      date: firstSummary.date,
      title: firstSummary.menuTitle,
    };
  }, [dateMode, reportData]);

  // Check if user is admin
  if (user?.role !== 'ADMIN') {
    return (
      <div className="container mx-auto p-8 max-w-6xl">
        <Card className="p-12 text-center">
          <p className="text-muted-foreground">
            You do not have permission to access this page.
          </p>
        </Card>
      </div>
    );
  }

  // Extract data for views
  const summaryData: RSVPSummaryItem[] = reportData?.summary || [];
  const detailsData: RSVPReportItem[] = reportData?.details || [];
  const byLocationData: RSVPLocationBreakdownItem[] = reportData?.byLocation || [];
  const cookingDayData: CookingDayReportData | null = reportData?.cookingDay || null;
  const meta = reportData?.meta;

  const hasData = dateMode === 'week'
    ? summaryData.length > 0
    : dateMode === 'day'
      ? detailsData.length > 0
      : (cookingDayData?.rows.length || 0) > 0;

  const periodLabel = dateMode === 'week'
    ? 'week'
    : dateMode === 'day'
      ? 'day'
      : 'cooking day';

  const selectionLabel = dateMode === 'week'
    ? 'week'
    : dateMode === 'day'
      ? 'date'
      : 'cooking day';

  return (
    <div className="container mx-auto p-4 md:p-8 max-w-7xl space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">RSVP Reports</h1>
          <p className="text-muted-foreground">
            {dateMode === 'week'
              ? 'Weekly summary for meal planning'
              : dateMode === 'day'
                ? 'Sign up sheet for meal distribution'
                : 'Combined sign up sheet grouped by cooking day'}
          </p>
        </div>
        <Button
          onClick={handleExport}
          disabled={exporting || loading || !hasData}
          className="w-full md:w-auto"
        >
          {exporting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div>
              Exporting...
            </>
          ) : (
            <>
              <Download className="h-4 w-4 mr-2" />
              Export XLSX
            </>
          )}
        </Button>
      </div>

      {/* Date Navigation */}
      <Card>
        <CardContent className="pt-6">
          <DateNavigator
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            mode={dateMode}
            onModeChange={handleModeChange}
            disabled={loading}
          />
        </CardContent>
      </Card>

      {/* Filters Row - Only show location filter in day-like report modes */}
      {isDayLikeMode && (
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
          {/* Location Filter */}
          <div className="w-full sm:w-64 space-y-2">
            <label className="text-sm font-medium">Filter by Pickup Location</label>
            <Select
              value={locationId?.toString() || 'ALL'}
              onValueChange={handleLocationChange}
              disabled={loading}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder="All Locations" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All Locations</SelectItem>
                {pickupLocations
                  .filter((loc) => loc.isActive)
                  .map((location) => (
                    <SelectItem key={location.id} value={location.id.toString()}>
                      <span className="flex items-center gap-2">
                        <LocationColorDot colorCode={location.colorCode} />
                        {location.name}
                      </span>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {/* Summary Stats */}
          {dateMode === 'day' && meta && (
            <div className="flex gap-3 ml-auto flex-wrap">
              <Badge variant="outline" className="text-sm">
                S: {meta.totalSmall}
              </Badge>
              <Badge variant="outline" className="text-sm">
                M: {meta.totalMedium}
              </Badge>
              <Badge variant="outline" className="text-sm">
                L: {meta.totalLarge}
              </Badge>
              <Badge variant="secondary" className="text-sm">
                Total: {meta.totalRsvps}
              </Badge>
            </div>
          )}
        </div>
      )}

      {/* Summary Stats for Full Week mode */}
      {dateMode === 'week' && meta && (
        <div className="flex gap-3 flex-wrap">
          <Badge variant="outline" className="text-sm">
            S: {meta.totalSmall}
          </Badge>
          <Badge variant="outline" className="text-sm">
            M: {meta.totalMedium}
          </Badge>
          <Badge variant="outline" className="text-sm">
            L: {meta.totalLarge}
          </Badge>
          <Badge variant="secondary" className="text-sm">
            Total Families: {meta.totalRsvps}
          </Badge>
        </div>
      )}

      {/* Report Content */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : !hasData ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <FileSpreadsheet className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                No RSVPs found for this {periodLabel}
                {locationId ? ` at ${getLocationName(locationId)}` : ''}.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Try selecting a different {selectionLabel}
                {isDayLikeMode ? ' or location' : ''}.
              </p>
            </div>
          ) : (
            <ReportTable
              view={activeTab}
              summaryData={summaryData}
              detailsData={detailsData}
              cookingDayData={cookingDayData}
              byLocationData={byLocationData}
              menuInfo={menuInfo}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
